import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Chip,
  Link,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import {
  CreditCard,
  Security,
  Settings,
  Info,
  CheckCircle,
  Warning,
  Link as LinkIcon,
  Code as CodeIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';

const CreditCardConfigDialog = ({ open, onClose, onSave, currentConfig = {} }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [config, setConfig] = useState({
    provider: 'stripe',
    enabled: false,
    testMode: true,
    stripe: {
      publicKey: '',
      secretKey: '',
      webhookSecret: '',
      connected: false
    },
    square: {
      applicationId: '',
      accessToken: '',
      locationId: '',
      connected: false
    },
    adyen: {
      merchantAccount: '',
      apiKey: '',
      clientKey: '',
      connected: false
    },
    paypal: {
      clientId: '',
      clientSecret: '',
      connected: false
    },
    fees: {
      percentage: 2.9,
      fixedAmount: 0.30,
      currency: 'USD'
    },
    security: {
      require3DS: true,
      saveCards: false,
      tokenization: true
    },
    ...currentConfig
  });

  const providers = [
    {
      id: 'stripe',
      name: 'Stripe',
      description: 'Most popular, supports 135+ currencies',
      pros: ['Easy integration', 'Global coverage', 'Excellent documentation'],
      fees: '2.9% + $0.30',
      regions: 'Global',
      popularity: 'Most Popular'
    },
    {
      id: 'square',
      name: 'Square',
      description: 'Great for small businesses, integrated hardware',
      pros: ['Free hardware', 'Inventory management', 'Easy setup'],
      fees: '2.6% + $0.10',
      regions: 'US, Canada, UK, Australia',
      popularity: 'SMB Favorite'
    },
    {
      id: 'adyen',
      name: 'Adyen',
      description: 'Enterprise-grade, single platform',
      pros: ['Enterprise features', 'Local payment methods', 'Advanced routing'],
      fees: 'Custom pricing',
      regions: 'Global',
      popularity: 'Enterprise'
    },
    {
      id: 'paypal',
      name: 'PayPal',
      description: 'Widely recognized, trusted by consumers',
      pros: ['Brand recognition', 'Buyer protection', 'Fast setup'],
      fees: '2.9% + $0.30',
      regions: 'Global',
      popularity: 'Consumer Trust'
    }
  ];

  useEffect(() => {
    if (currentConfig && Object.keys(currentConfig).length > 0) {
      setConfig(prevConfig => ({ ...prevConfig, ...currentConfig }));
    }
  }, [currentConfig]);

  const handleProviderChange = (providerId) => {
    setConfig(prev => ({ ...prev, provider: providerId }));
  };

  const handleConfigChange = (section, field, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleTestConnection = async (provider) => {
    // Simulate connection test
    setTimeout(() => {
      setConfig(prev => ({
        ...prev,
        [provider]: {
          ...prev[provider],
          connected: true
        }
      }));
    }, 2000);
  };

  const handleSave = () => {
    onSave({
      ...config,
      integrationConfigured: config[config.provider].connected,
      lastUpdated: new Date().toISOString()
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CreditCard />
          <Typography variant="h6">Credit Card Integration Setup</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Provider Selection" />
          <Tab label="Configuration" />
          <Tab label="Security & Fees" />
          <Tab label="Testing" />
        </Tabs>

        {/* Tab 1: Provider Selection */}
        {activeTab === 0 && (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Choose your credit card processing provider. Each provider has different features, 
              pricing, and regional availability.
            </Typography>

            <Grid container spacing={2}>
              {providers.map((provider) => (
                <Grid item xs={12} md={6} key={provider.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      border: config.provider === provider.id ? 2 : 1,
                      borderColor: config.provider === provider.id ? 'primary.main' : 'grey.300',
                      '&:hover': { borderColor: 'primary.main' },
                      position: 'relative'
                    }}
                    onClick={() => handleProviderChange(provider.id)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6">{provider.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {provider.description}
                          </Typography>
                        </Box>
                        {config.provider === provider.id && (
                          <CheckCircle color="primary" />
                        )}
                        <Chip 
                          label={provider.popularity} 
                          size="small" 
                          color="secondary"
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        {provider.pros.map((pro, index) => (
                          <Chip key={index} label={pro} size="small" variant="outlined" />
                        ))}
                      </Box>

                      <Box sx={{ display: 'flex', justify: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2">
                          <strong>Fees:</strong> {provider.fees}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Regions:</strong> {provider.regions}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Recommendation:</strong> Stripe is recommended for most businesses due to its 
                ease of integration, global coverage, and comprehensive documentation.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Tab 2: Provider Configuration */}
        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {providers.find(p => p.id === config.provider)?.name} Configuration
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={config.testMode}
                  onChange={(e) => setConfig(prev => ({ ...prev, testMode: e.target.checked }))}
                />
              }
              label="Test Mode (Use sandbox credentials)"
              sx={{ mb: 3 }}
            />

            {/* Stripe Configuration */}
            {config.provider === 'stripe' && (
              <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Publishable Key"
                  value={config.stripe.publicKey}
                  onChange={(e) => handleConfigChange('stripe', 'publicKey', e.target.value)}
                  placeholder={config.testMode ? "pk_test_..." : "pk_live_..."}
                  helperText="Found in your Stripe Dashboard > Developers > API keys"
                />
                
                <TextField
                  fullWidth
                  label="Secret Key"
                  type="password"
                  value={config.stripe.secretKey}
                  onChange={(e) => handleConfigChange('stripe', 'secretKey', e.target.value)}
                  placeholder={config.testMode ? "sk_test_..." : "sk_live_..."}
                  helperText="Keep this secure and never share publicly"
                />
                
                <TextField
                  fullWidth
                  label="Webhook Secret"
                  value={config.stripe.webhookSecret}
                  onChange={(e) => handleConfigChange('stripe', 'webhookSecret', e.target.value)}
                  placeholder="whsec_..."
                  helperText="Required for secure webhook verification"
                />

                <Button
                  variant="contained"
                  onClick={() => handleTestConnection('stripe')}
                  disabled={!config.stripe.publicKey || !config.stripe.secretKey}
                  startIcon={config.stripe.connected ? <CheckCircle /> : <Settings />}
                  color={config.stripe.connected ? 'success' : 'primary'}
                >
                  {config.stripe.connected ? 'Connected' : 'Test Connection'}
                </Button>

                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Info color="primary" />
                    Setup Instructions
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
                      <ListItemText 
                        primary={<Link href="https://stripe.com" target="_blank">Create a Stripe account</Link>}
                        secondary="Complete business verification"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                      <ListItemText 
                        primary="Get API keys"
                        secondary="Dashboard > Developers > API keys"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><VerifiedIcon fontSize="small" /></ListItemIcon>
                      <ListItemText 
                        primary="Configure webhooks"
                        secondary="For real-time payment updates"
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Box>
            )}

            {/* Similar configurations for other providers... */}
          </Box>
        )}

        {/* Tab 3: Security & Fees */}
        {activeTab === 2 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Security Settings</Typography>
            
            <Box sx={{ display: 'grid', gap: 2, mb: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.security.require3DS}
                    onChange={(e) => handleConfigChange('security', 'require3DS', e.target.checked)}
                  />
                }
                label="Require 3D Secure Authentication"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={config.security.saveCards}
                    onChange={(e) => handleConfigChange('security', 'saveCards', e.target.checked)}
                  />
                }
                label="Allow Customers to Save Cards"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={config.security.tokenization}
                    onChange={(e) => handleConfigChange('security', 'tokenization', e.target.checked)}
                  />
                }
                label="Use Card Tokenization"
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ mb: 2 }}>Processing Fees</Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Percentage Fee (%)"
                  type="number"
                  value={config.fees.percentage}
                  onChange={(e) => handleConfigChange('fees', 'percentage', parseFloat(e.target.value))}
                  inputProps={{ step: 0.1, min: 0, max: 10 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Fixed Fee Amount"
                  type="number"
                  value={config.fees.fixedAmount}
                  onChange={(e) => handleConfigChange('fees', 'fixedAmount', parseFloat(e.target.value))}
                  inputProps={{ step: 0.01, min: 0 }}
                />
              </Grid>
            </Grid>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                These fees are for reference only. Actual fees are determined by your payment processor.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Tab 4: Testing */}
        {activeTab === 3 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Test Integration</Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Use these test card numbers to verify your integration is working correctly.
              </Typography>
            </Alert>

            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Test Card Numbers</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Visa:</strong> 4242424242424242</Typography>
                  <Typography variant="body2"><strong>Mastercard:</strong> 5555555555554444</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>American Express:</strong> 378282246310005</Typography>
                  <Typography variant="body2"><strong>Declined Card:</strong> 4000000000000002</Typography>
                </Grid>
              </Grid>
            </Paper>

            <Button
              variant="outlined"
              fullWidth
              disabled={!config[config.provider].connected}
              sx={{ mt: 2 }}
            >
              Run Test Transaction
            </Button>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!config[config.provider].connected}
        >
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreditCardConfigDialog;